"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTheme } from "./ThemeProvider";
import { AnnouncementBar, AnnouncementBarSettings } from "./AnnouncementBar";
import type { MegaMenuConfig } from "./MegaMenu";
import { businessData } from "@/data/business";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface SiteHeaderProps {
  announcement?: AnnouncementBarSettings & { source?: "global" | "page" };
  initialSettings?: Record<string, any> | null;
  showAnnouncement?: boolean;
}

interface NavLinkSetting {
  label: string;
  href: string;
  megaMenu?: MegaMenuConfig;
}

function ConfiguredMegaContent({ config, parentHref }: { config: MegaMenuConfig; parentHref: string }) {
  if (config.type === "categories") {
    const midpoint = Math.ceil(businessData.categories.length / 2);
    const columns = [businessData.categories.slice(0, midpoint), businessData.categories.slice(midpoint)];
    return (
      <div className="mega-grid mega-grid-wide">
        <div className="mega-links mega-cols-2">
          {columns.map((categories, columnIndex) => (
            <div className="mega-col" key={columnIndex}>
              <h4>{columnIndex === 0 ? "Formulation Suites" : "More Categories"}</h4>
              <ul>
                {categories.map((category) => <li key={category.id}><Link href={category.href}>{category.title}</Link></li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="mega-editorial mega-config-highlight">
          {config.featuredProductSlug ? (
            <Link href={`/products/${config.featuredProductSlug}`}>
              <span>Featured formulation</span>
              <strong>View selected product</strong>
            </Link>
          ) : (
            <Link href="/products">
              <span>All formulations</span>
              <strong>Browse the catalog</strong>
            </Link>
          )}
        </div>
      </div>
    );
  }

  if (config.type === "page-preview") {
    const imageUrl = resolveMediaUrl(config.featuredImage, "/assets/categories/kits-v2.jpg");
    return (
      <div className="mega-grid mega-config-preview">
        <div className="mega-links">
          <h4>Explore this section</h4>
          <p>Review our manufacturing capabilities, product options, quality controls, and project process.</p>
          <Link href={parentHref}>Open page</Link>
        </div>
        <div className="mega-editorial"><img src={imageUrl} alt="" /></div>
      </div>
    );
  }

  const columns = (config.customColumns || []).slice(0, 3);
  return (
    <div className="mega-config-columns" style={{ "--mega-columns": Math.max(columns.length, 1) } as React.CSSProperties}>
      {columns.map((column, columnIndex) => (
        <div className="mega-links" key={`${column.heading}-${columnIndex}`}>
          <h4>{column.heading}</h4>
          <ul>
            {column.items.slice(0, 12).map((item, itemIndex) => {
              const href = item.href.startsWith("/") || item.href.startsWith("https://") ? item.href : parentHref;
              return (
                <li key={`${item.label}-${itemIndex}`}>
                  <Link href={href}>
                    <span>{item.label}</span>
                    {item.description && <small>{item.description}</small>}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

const defaultNavLinks: NavLinkSetting[] = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Services", href: "/services" },
  { label: "Product Range", href: "/product-range" },
  { label: "How We Work", href: "/process" },
  { label: "Blogs", href: "/blogs" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

export function SiteHeader({
  announcement,
  initialSettings,
  showAnnouncement = true,
}: SiteHeaderProps = {}) {
  const pathname = usePathname();
  const { theme, setTheme } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState<string | null>(null);

  const [scrolled, setScrolled] = useState(false);
  const globalAnnouncement = initialSettings?.announcementBar || initialSettings?.announcement_bar;
  const announcementSettings = announcement?.source === "page"
    ? announcement
    : globalAnnouncement || announcement;
  const logoText = initialSettings?.logoText || "Shaaira Biocare";
  const logoParts = String(logoText).trim().split(/\s+/);
  const logoImage = resolveMediaUrl(initialSettings?.logoImage, "/assets/logos/shaaira-biocare-logo-icon.svg");
  const contactText = initialSettings?.contactText || "Get a Quote";
  const configuredNavLinks = Array.isArray(initialSettings?.navLinks)
    ? initialSettings.navLinks
        .filter((item: unknown): item is NavLinkSetting => {
          if (!item || typeof item !== "object") return false;
          const link = item as Partial<NavLinkSetting>;
          return typeof link.label === "string" && typeof link.href === "string";
        })
        .map((item: NavLinkSetting) => ({ label: item.label.trim(), href: item.href.trim(), megaMenu: item.megaMenu }))
        .filter((item: NavLinkSetting) => item.label && (item.href.startsWith("/") || item.href.startsWith("https://")))
        .slice(0, 12)
    : [];
  const navLinks = configuredNavLinks.length > 0 ? configuredNavLinks : defaultNavLinks;
  const findNav = (...hrefs: string[]) => navLinks.find((item) => hrefs.includes(item.href.replace(/\/$/, "") || "/"));
  const navOrder = (item?: NavLinkSetting) => item ? navLinks.indexOf(item) : 0;
  const homeNav = findNav("/");
  const aboutNav = findNav("/about");
  const servicesNav = findNav("/services");
  const productsNav = findNav("/product-range", "/products");
  const processNav = findNav("/process");
  const blogsNav = findNav("/blogs");
  const faqNav = findNav("/faq");
  const contactNav = findNav("/contact");
  const reservedNavHrefs = new Set(["/", "/about", "/services", "/product-range", "/products", "/process", "/blogs", "/faq", "/contact"]);
  const customNavLinks = navLinks.filter((item) => !reservedNavHrefs.has(item.href.replace(/\/$/, "") || "/"));
  const servicesMegaConfig = servicesNav?.megaMenu?.enabled ? servicesNav.megaMenu : null;
  const productsMegaConfig = productsNav?.megaMenu?.enabled ? productsNav.megaMenu : null;

  // Close mobile nav on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenMega(null);
  }, [pathname]);

  // Scroll elevation effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 12);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.classList.add("nav-lock");
      document.documentElement.classList.add("nav-lock");
    } else {
      document.body.classList.remove("nav-lock");
      document.documentElement.classList.remove("nav-lock");
    }
  }, [mobileOpen]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || !mobileOpen) return;
      setMobileOpen(false);
      document.querySelector<HTMLButtonElement>(".nav-toggle")?.focus();
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [mobileOpen]);

  const isActive = (path: string) => {
    if (path === "./" || path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };
  const productNavActive = ["/product-range", "/products", "/skin-care", "/hair-care", "/body-care", "/baby-mom-care", "/men-range", "/oral-care", "/pet-care", "/other-care", "/kits"]
    .some((path) => isActive(path));

  return (
    <>
      {showAnnouncement && (
        announcementSettings ? <AnnouncementBar {...announcementSettings} /> : <AnnouncementBar />
      )}
      <header
      className="site-header"
      style={{
        boxShadow: scrolled ? "0 8px 30px rgba(4, 26, 20, 0.10)" : "none",
        transition: "box-shadow 0.3s cubic-bezier(0.2, 0.8, 0.2, 1), background 0.3s",
      }}
    >
      <div className="container header-inner">
        <Link className="brand" href="/" aria-label="Shaaira Biocare Home">
          <img
            src={logoImage}
            alt=""
            className="brand-mark"
            width={44}
            height={44}
          />
          <span className="brand-text">
            <strong>{logoParts[0] || "Shaaira"}</strong>
            <strong>{logoParts.slice(1).join(" ") || "Biocare"}</strong>
          </span>
        </Link>

        <nav id="primary-navigation" className={`site-nav ${mobileOpen ? "open" : ""}`} aria-label="Primary">
          {homeNav && <Link href={homeNav.href} style={{ order: navOrder(homeNav) }} className={isActive("/") ? "active" : ""}>{homeNav.label}</Link>}
          {aboutNav && <Link href={aboutNav.href} style={{ order: navOrder(aboutNav) }} className={isActive("/about") ? "active" : ""}>{aboutNav.label}</Link>}

          {/* Services Mega Menu */}
          {servicesNav && (
          <div
            className={`nav-item has-mega ${openMega === "services" ? "is-open" : ""}`}
            style={{ order: navOrder(servicesNav) }}
          >
            <Link
              href={servicesNav.href}
              className={`nav-link ${isActive("/services") ? "active" : ""}`}
              onClick={(e) => {
                if (window.innerWidth <= 1180) {
                  e.preventDefault();
                  setOpenMega(openMega === "services" ? null : "services");
                }
              }}
            >
              {servicesNav.label}{" "}
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </Link>
            <div className="mega-menu">
              {servicesMegaConfig ? <ConfiguredMegaContent config={servicesMegaConfig} parentHref={servicesNav.href} /> : (
              <div className="mega-grid">
                <div className="mega-links">
                  <h4>Capabilities</h4>
                  <ul>
                    <li>
                      <Link href="/services#custom-formulation">Custom Formulation R&D</Link>
                    </li>
                    <li>
                      <Link href="/services#private-label">Private Label Manufacturing</Link>
                    </li>
                    <li>
                      <Link href="/services#regulatory">Regulatory Documentation</Link>
                    </li>
                    <li>
                      <Link href="/services#packaging">Packaging & Design Procurement</Link>
                    </li>
                  </ul>
                </div>
                <div className="mega-editorial">
                  <div className="mega-card">
                    <img src="/og-lab-v2.jpg" alt="" loading="lazy" />
                    <div className="mega-card-content">
                      <span>Technical Development</span>
                      <strong>Specification-Led Formulation</strong>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          )}

          {/* Product Range Mega Menu */}
          {productsNav && (
          <div
            className={`nav-item has-mega ${openMega === "products" ? "is-open" : ""}`}
            style={{ order: navOrder(productsNav) }}
          >
            <Link
              href={productsNav.href}
              className={`nav-link ${productNavActive ? "active" : ""}`}
              onClick={(e) => {
                if (window.innerWidth <= 1180) {
                  e.preventDefault();
                  setOpenMega(openMega === "products" ? null : "products");
                }
              }}
            >
              {productsNav.label}{" "}
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </Link>
            <div className="mega-menu">
              {productsMegaConfig ? <ConfiguredMegaContent config={productsMegaConfig} parentHref={productsNav.href} /> : (
              <div className="mega-grid mega-grid-wide">
                <div className="mega-links mega-cols-2">
                  <div className="mega-col">
                    <h4>Formulation Suites</h4>
                    <ul>
                      <li>
                        <Link href="/skin-care">Advanced Skin Care</Link>
                      </li>
                      <li>
                        <Link href="/hair-care">Scalp & Hair Therapeutics</Link>
                      </li>
                      <li>
                        <Link href="/body-care">Body & Dermal Wellness</Link>
                      </li>
                      <li>
                        <Link href="/baby-mom-care">Pediatric & Maternity</Link>
                      </li>
                    </ul>
                  </div>
                  <div className="mega-col">
                    <h4>&nbsp;</h4>
                    <ul>
                      <li>
                        <Link href="/men-range">Men's Grooming</Link>
                      </li>
                      <li>
                        <Link href="/other-care">Suncare & Hygiene</Link>
                      </li>
                      <li>
                        <Link href="/kits">Curated Bundles</Link>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mega-editorial">
                  <div className="mega-card">
                    <img src="/assets/categories/kits-v2.jpg" alt="" loading="lazy" />
                    <div className="mega-card-content">
                      <span>Formulation Catalogue</span>
                      <strong>42 Starting Product Directions</strong>
                    </div>
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
          )}

          {processNav && <Link href={processNav.href} style={{ order: navOrder(processNav) }} className={isActive("/process") ? "active" : ""}>{processNav.label}</Link>}
          {blogsNav && <Link href={blogsNav.href} style={{ order: navOrder(blogsNav) }} className={isActive("/blogs") ? "active" : ""}>{blogsNav.label}</Link>}
          {faqNav && <Link href={faqNav.href} style={{ order: navOrder(faqNav) }} className={isActive("/faq") ? "active" : ""}>{faqNav.label}</Link>}
          {contactNav && <Link href={contactNav.href} style={{ order: navOrder(contactNav) }} className={isActive("/contact") ? "active" : ""}>{contactNav.label}</Link>}
          {customNavLinks.map((item, index) => {
            const menuKey = `custom-${index}`;
            if (item.megaMenu?.enabled) {
              return (
                <div className={`nav-item has-mega ${openMega === menuKey ? "is-open" : ""}`} style={{ order: navOrder(item) }} key={`${item.href}-${item.label}`}>
                  <Link
                    href={item.href}
                    className={`nav-link ${item.href.startsWith("/") && isActive(item.href) ? "active" : ""}`}
                    onClick={(event) => {
                      if (window.innerWidth <= 1180) {
                        event.preventDefault();
                        setOpenMega(openMega === menuKey ? null : menuKey);
                      }
                    }}
                  >
                    {item.label}
                    <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" fill="none" strokeWidth="2" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                  </Link>
                  <div className="mega-menu"><ConfiguredMegaContent config={item.megaMenu} parentHref={item.href} /></div>
                </div>
              );
            }
            return (
              <Link key={`${item.href}-${item.label}`} href={item.href} style={{ order: navOrder(item) }} className={item.href.startsWith("/") && isActive(item.href) ? "active" : ""}>
                {item.label}
              </Link>
            );
          })}

          {/* Mobile Nav Footer */}
          <div className="mobile-nav-footer">
            <div className="theme-switch theme-switch--drawer" role="group" aria-label="Color theme">
              <button
                type="button"
                className="theme-opt"
                aria-pressed={theme === "system"}
                onClick={() => setTheme("system")}
                title="System"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                <span>System</span>
              </button>
              <button
                type="button"
                className="theme-opt"
                aria-pressed={theme === "light"}
                onClick={() => setTheme("light")}
                title="Light"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="4" />
                  <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
                <span>Light</span>
              </button>
              <button
                type="button"
                className="theme-opt"
                aria-pressed={theme === "dark"}
                onClick={() => setTheme("dark")}
                title="Dark"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 14.3A9 9 0 1 1 9.7 3 7 7 0 0 0 21 14.3z" />
                </svg>
                <span>Dark</span>
              </button>
            </div>

            <Link href="/products" className="catalog-download" onClick={() => setMobileOpen(false)}>
              <img src="/assets/categories/kits-v2.jpg" alt="Unbranded private-label discovery kit" />
              <div>
                <strong>Browse Formulation Catalog</strong>
                <span>42 formulations • 9 categories</span>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </Link>
          </div>
        </nav>

        <div className="header-actions">
          <div className="theme-switch theme-switch--bar" role="group" aria-label="Color theme">
            <button
              type="button"
              className="theme-opt"
              aria-pressed={theme === "system"}
              onClick={() => setTheme("system")}
              title="System"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            </button>
            <button
              type="button"
              className="theme-opt"
              aria-pressed={theme === "light"}
              onClick={() => setTheme("light")}
              title="Light"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            </button>
            <button
              type="button"
              className="theme-opt"
              aria-pressed={theme === "dark"}
              onClick={() => setTheme("dark")}
              title="Dark"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 14.3A9 9 0 1 1 9.7 3 7 7 0 0 0 21 14.3z" />
              </svg>
            </button>
          </div>

          <Link className="btn btn-primary btn-sm" href="/contact">
            {contactText}{" "}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </Link>

          <button
            className="nav-toggle"
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            aria-controls="primary-navigation"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
      </header>
    </>
  );
}
